import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import axiosInstance from '../../../../api/axiosInstance';
import { createInterview, updateInterview, fetchJobList, createProject, fetchProjectList, updateProject } from '../../../../api/candidiate';
import { formatDate, getUserInfoDetails } from '../../../../utlis';
import useFetch from '../../../../hooks/useFetch';
import StarRating from '../../../../components/StarRating';
import CandidateInterviewsHeader from './components/CandidateInterviewsHeader';
import CandidateInterviewsTable from './components/CandidateInterviewsTable';
import CandidateInterviewsModal from './components/CandidateInterviewsModal';
import {
  initialCandidateInterview,
  filterCategories,
  prepareInterviewForEdit,
  prepareInterviewPayload,
} from './candidateInterviewUtils';

const { companyId, clientId } = getUserInfoDetails();

const Interviews = () => {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onChange',
    defaultValues: initialCandidateInterview,
  });

  // Get candidateId from Redux store
  const candidateId = useSelector((state) => state.personal.candidateId);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [originalInterviewStatus, setOriginalInterviewStatus] = useState('');

  // Read-only when interview is "Selected" and user is not ADMIN or MANAGER
  const userInfo = getUserInfoDetails();
  const userRole = (userInfo?.role || '').toUpperCase();
  const isReadOnly = originalInterviewStatus === 'Selected' && !['ADMIN', 'MANAGER'].includes(userRole);

  // Loading states
  const [isLoading, setLoading] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);

  // Data state
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState('');
  const [selectedJobDetails, setSelectedJobDetails] = useState(null);
  const [pageSize, setPageSize] = useState(15);
  const [searchTerm, setSearchTerm] = useState('');
  // salesPersons now derived from useFetch below

  // Filter state
  const [isFilterPopupOpen, setFilterPopupOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState([]);

  // Reference data
  const { data: refVendor, loading: refVendorLoading } = useFetch('/vendors');
  const { data: refTechnologies, loading: refTechnologiesLoading } = useFetch('/technology', {
    company: companyId,
    client: clientId,
  });
  const { data: refSalesPerson, loading: salesPersonsLoading } = useFetch('/salesteam', {
    company: companyId,
    client: clientId,
  });
  const salesPersons = refSalesPerson?.data?.records || [];

  // Fetch jobs for this candidate with status = Active or Assigned
  useEffect(() => {
    const fetchJobs = async () => {
      if (!candidateId) {
        setJobs([]);
        return;
      }
      try {
        const res = await fetchJobList({ candidateId });
        const allJobs = res.data.data.records || [];
        const filtered = allJobs.filter(
          (j) => j.status === 'Active' || j.status === 'Assigned'
        );
        setJobs(filtered);
      } catch (err) {
        console.error('Error fetching jobs:', err);
        setJobs([]);
      }
    };
    fetchJobs();
  }, [candidateId]);


  // Modal handlers
  const openModal = () => {
    reset(initialCandidateInterview);
    setSelectedJob('');
    setSelectedJobDetails(null);
    setOriginalInterviewStatus('');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    reset(initialCandidateInterview);
    setSelectedJob('');
    setOriginalInterviewStatus('');
  };

  const prepareUpdate = (recordId) => {
    try {
      const interview = data.find((d) => d.record_id === recordId);
      if (!interview) {
        throw new Error('Interview not found');
      }

      const preparedData = prepareInterviewForEdit(interview);
      setOriginalInterviewStatus(interview.scheduling_status || interview.status || '');
      setSelectedJob(interview.job_id);
      // Set job details for read-only display in edit mode
      const jobRecord = jobs.find((j) => String(j.job_id) === String(interview.job_id));
      if (jobRecord) {
        setSelectedJobDetails({
          jobTitle: jobRecord.job_title || '',
          technologyName: jobRecord.technology_desc || '',
          vendorName: jobRecord.vendor_name || '',
          clientName: jobRecord.end_client_name || '',
          salesPersonName: jobRecord.sales_person_name || '',
        });
        // Fallback to job record if interview doesn't have these values
        if (!preparedData.masterJobId) {
          preparedData.masterJobId = jobRecord.master_job_id || '';
        }
        if (!preparedData.marketingId) {
          preparedData.marketingId = jobRecord.marketing_id || jobRecord.record_id || '';
        }
      } else {
        setSelectedJobDetails(null);
      }
      reset(preparedData);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error in prepareUpdate:', error);
      toast.error(error.message || 'An error occurred while preparing the update');
    }
  };

  // Job change handler
  const handleJobChange = (event) => {
    const jobId = event.target.value;
    setSelectedJob(jobId);
    const selectedJobDetails = jobs.find((job) => String(job.job_id) === String(jobId));
    if (selectedJobDetails) {
      setValue('jobTitle', selectedJobDetails.job_title);
      setValue('technology', selectedJobDetails.technology_id);
      setValue('vendorId', selectedJobDetails.vendor_id);
      setValue('clientCompany', selectedJobDetails.end_client_name || '');
      setValue('salesPersonId', selectedJobDetails.sales_person_id);
      setValue('masterJobId', selectedJobDetails.master_job_id || '');
      setValue('marketingId', selectedJobDetails.marketing_id || selectedJobDetails.record_id || '');
      // Store display names for read-only fields
      setSelectedJobDetails({
        jobTitle: selectedJobDetails.job_title || '',
        technologyName: selectedJobDetails.technology_desc || '',
        vendorName: selectedJobDetails.vendor_name || '',
        clientName: selectedJobDetails.end_client_name || '',
        salesPersonName: selectedJobDetails.sales_person_name || '',
      });
    } else {
      setSelectedJobDetails(null);
      reset({
        ...initialCandidateInterview,
        jobTitle: '',
        technology: '',
        vendorId: '',
        clientCompany: '',
        salesPersonId: '',
      });
    }
  };

  // Form submission
  const onSubmit = async (formData) => {
    if (!candidateId) {
      toast.error('Please select a candidate first');
      return;
    }

    setSubmitting(true);
    try {
      const payload = prepareInterviewPayload(formData, candidateId);

      if (formData.interviewId) {
        // Get original interview status before update
        const originalInterview = data.find((d) => String(d.record_id) === String(formData.interviewId));
        const originalStatus = originalInterview?.scheduling_status || originalInterview?.status || '';


        await updateInterview({ ...payload, interviewId: formData.interviewId }, candidateId);
        toast.success('Interview Updated Successfully');

        // Auto-create or update project when interview status is updated to "Selected"
        if (formData.schedulingStatus === 'Selected' && originalStatus !== 'Selected') {
          try {
            const jobRecord = jobs.find((j) => String(j.job_id) === String(formData.jobId));
            const projectData = {
              projectStatus: 'Not Started',
              jobId: formData.jobId,
              interviewId: formData.interviewId,
              endClientName: jobRecord?.end_client_name || '',
              vendorId: formData.vendorId || jobRecord?.vendor_id,
              salesMgrId: formData.salesPersonId || jobRecord?.sales_person_id,
              city: jobRecord?.city || '',
              state: jobRecord?.state || '',
              billingRate: jobRecord?.bill_rate,
              payType: jobRecord?.pay_type || '',
            };

            const existingProjectId = originalInterview?.project_id || formData.projectId;
            if (existingProjectId) {
              await updateProject({ ...projectData, candidateId }, existingProjectId);
              toast.success('Project Updated Successfully');
            } else {
              await createProject(projectData, candidateId);
              toast.success('Project Created Successfully');
            }
          } catch (projError) {
            console.error('Error saving project:', projError);
            const responseData = projError?.response?.data;
            const statusCode = projError?.response?.status;
            if (statusCode === 409) {
              toast.warning(responseData?.message || 'A project already exists for this candidate and job');
            } else {
              const errMsg = responseData?.details || responseData?.message || projError.message;
              toast.error(errMsg || 'Interview updated but failed to save project');
            }
          }
        }

        // Update project to On Hold when interview status changed FROM "Selected" to something else
        if (originalStatus === 'Selected' && formData.schedulingStatus !== 'Selected') {
          try {
            const jobRecord = jobs.find((j) => String(j.job_id) === String(formData.jobId));
            const projRes = await fetchProjectList({ candidateId });
            const projects = projRes.data?.data?.records || [];
            const matchingProject = projects.find(
              (p) => String(p.interview_id) === String(formData.interviewId)
            );
            if (matchingProject) {
              await updateProject(
                {
                  projectStatus: 'On Hold',
                  candidateId,
                  jobId: formData.jobId,
                  interviewId: formData.interviewId,
                  endClientName: jobRecord?.end_client_name || '',
                  vendorId: formData.vendorId || jobRecord?.vendor_id,
                  salesMgrId: formData.salesPersonId || jobRecord?.sales_person_id,
                  city: jobRecord?.city || '',
                  state: jobRecord?.state || '',
                  billingRate: jobRecord?.bill_rate,
                  payType: jobRecord?.pay_type || '',
                },
                matchingProject.project_id || matchingProject.record_id
              );
              toast.success('Project status updated to On Hold');
            }
          } catch (projError) {
            console.error('Error updating project to On Hold:', projError);
            const errMsg = projError?.response?.data?.details || projError?.response?.data?.message || projError.message;
            toast.error(errMsg || 'Interview updated but failed to update project status');
          }
        }
      } else {
        await createInterview(payload, candidateId);
        toast.success('Interview Added Successfully');
      }
      closeModal();
      fetchInterviews();
    } catch (error) {
      console.error('Error saving interview:', error);
      toast.error('Something Went Wrong');
    } finally {
      setSubmitting(false);
    }
  };

  // Fetch data
  const fetchInterviews = async () => {
    if (!candidateId) {
      setData([]);
      return;
    }
    setLoading(true);
    try {
      const res = await axiosInstance.get(
        `/interview?client=${clientId}&company=${companyId}&candidateId=${candidateId}`
      );
      setData(res.data.data.records || []);
    } catch (err) {
      console.error('Error fetching interviews:', err);
      toast.error('Something Went Wrong');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInterviews();
  }, [candidateId]);

  // Search and filter handlers
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterClick = () => {
    setFilterPopupOpen(true);
  };

  const handleCloseFilterPopup = () => {
    setFilterPopupOpen(false);
  };

  const handleApplyFilters = (filters) => {
    setAppliedFilters(filters || []);
    setFilterPopupOpen(false);
  };

  // Filter search results for FilterPopup
  const getSearchResults = (searchTerm, selectedCategory) => {
    if (!searchTerm || !selectedCategory || !data) return [];

    return data
      .filter((item) => {
        let valueToSearch = '';

        switch (selectedCategory) {
          case 'Job':
            valueToSearch = item.job_title || '';
            break;
          case 'Technology':
            valueToSearch = item.technology_name || '';
            break;
          case 'Client':
            valueToSearch = item.client || '';
            break;
          case 'Vendor':
            valueToSearch = item.vendor_name || '';
            break;
          case 'Interviewer':
            valueToSearch = item.interview_panel_details || '';
            break;
          case 'Rating':
            valueToSearch = item.rating?.toString() || '';
            break;
          case 'Status':
            valueToSearch = item.scheduling_status || item.status || '';
            break;
          default:
            valueToSearch = '';
        }

        return valueToSearch.toLowerCase().includes(searchTerm.toLowerCase());
      })
      .map((item) => {
        switch (selectedCategory) {
          case 'Job':
            return item.job_title;
          case 'Technology':
            return item.technology_name;
          case 'Client':
            return item.client;
          case 'Vendor':
            return item.vendor_name;
          case 'Interviewer':
            return item.interview_panel_details;
          case 'Rating':
            return item.rating?.toString();
          case 'Status':
            return item.scheduling_status || item.status;
          default:
            return null;
        }
      })
      .filter(Boolean)
      .filter((value, index, self) => self.indexOf(value) === index);
  };

  // Apply search and filters to data
  useEffect(() => {
    if (!data.length) {
      setFilteredData([]);
      return;
    }

    let result = data;

    // Apply search filter first
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter((item) => {
        return (
          (item.job_title || '').toLowerCase().includes(searchLower) ||
          (item.technology_name || '').toLowerCase().includes(searchLower) ||
          (item.client || '').toLowerCase().includes(searchLower) ||
          (item.vendor_name || '').toLowerCase().includes(searchLower) ||
          (item.interview_panel_details || '').toLowerCase().includes(searchLower) ||
          (item.feedback || '').toLowerCase().includes(searchLower)
        );
      });
    }

    // Apply category filters
    if (Array.isArray(appliedFilters) && appliedFilters.length > 0) {
      result = result.filter((item) => {
        return appliedFilters.some(({ category, items }) => {
          if (!items || items.length === 0) return false;

          switch (category) {
            case 'Job':
              return items.some((title) => title.toLowerCase() === (item.job_title || '').toLowerCase());
            case 'Technology':
              return items.some((tech) => tech.toLowerCase() === (item.technology_name || '').toLowerCase());
            case 'Client':
              return items.some((client) => client.toLowerCase() === (item.client || '').toLowerCase());
            case 'Vendor':
              return items.some((vendor) => vendor.toLowerCase() === (item.vendor_name || '').toLowerCase());
            case 'Interviewer':
              return items.some((interviewer) =>
                (item.interview_panel_details || '').toLowerCase().includes(interviewer.toLowerCase())
              );
            case 'Rating':
              return items.some((rating) => rating === item.rating?.toString());
            case 'Status':
              return items.some(
                (status) =>
                  status.toLowerCase() === (item.scheduling_status || item.status || '').toLowerCase()
              );
            default:
              return false;
          }
        });
      });
    }

    setFilteredData(result);
  }, [appliedFilters, data, searchTerm]);

  // Table columns configuration
  const columns = [
    {
      name: 'record_id',
      label: 'ID',
      options: { display: false },
    },
    {
      name: 'job_title',
      label: 'Job',
      options: {
        customBodyRender: (value, tableMeta) => (
          <h3
            onClick={() => prepareUpdate(tableMeta.rowData[0])}
            className="text-base font-semibold text-auxiliary-800 cursor-pointer"
          >
            {value}
          </h3>
        ),
      },
    },
    {
      name: 'technology_name',
      label: 'Technology',
      options: { filter: true, sort: true },
    },
    {
      name: 'client',
      label: 'Client',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value, tableMeta) => {
          const client = tableMeta.rowData[tableMeta.columnIndex];
          const company = tableMeta.rowData[tableMeta.columnIndex + 1];
          return (
            <>
              <h3>{client}</h3>
              <p className="text-xs text-text-hint">{company}</p>
            </>
          );
        },
      },
    },
    {
      name: 'vendor_name',
      label: 'Vendor',
      options: { filter: true, sort: true },
    },
    {
      name: 'interview_date',
      label: 'Date',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => formatDate(value),
      },
    },
    {
      name: 'bill_rate',
      label: 'Bill Rate',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => (value ? `${value} USD` : '-'),
      },
    },
    {
      name: 'interview_panel_details',
      label: 'Interviewer',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          if (!value) return '';
          const truncatedValue =
            value.split(' ').slice(0, 2).join(' ') + (value.split(' ').length > 2 ? '...' : '');
          return <span title={value}>{truncatedValue}</span>;
        },
      },
    },
    {
      name: 'rating',
      label: 'Rating',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => <StarRating fontSize={16} initialRating={parseInt(value) || 0} readOnly />,
      },
    },
    {
      name: 'scheduling_status',
      label: 'Status',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => (
          <span className="font-bold">{value || '-'}</span>
        ),
      },
    },
  ];

  // Table options
  const options = {
    filterType: 'dropdown',
    responsive: 'standard',
    rowsPerPage: pageSize,
    rowsPerPageOptions: [5, 10, 15, 20, 50],
    onChangeRowsPerPage: (numberOfRows) => setPageSize(numberOfRows),
    selectableRows: 'none',
    elevation: 0,
    filter: true,
    search: false,
    download: true,
    print: true,
    viewColumns: true,
    textLabels: {
      body: {
        noMatch: !isLoading && (!filteredData || filteredData.length === 0) ? 'Sorry, no matching found' : '',
      },
    },
  };

  return (
    <div className="p-5">
      <div className="border border-gray-300 rounded-lg relative">
        {/* Header */}
        <CandidateInterviewsHeader
          onReload={fetchInterviews}
          onAddInterview={openModal}
          isLoading={isLoading}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
        />

        {/* Table with loading overlay */}
        <div className="relative">
          <CandidateInterviewsTable
            data={isLoading ? [] : filteredData}
            columns={columns}
            options={options}
            isFilterPopupOpen={isFilterPopupOpen}
            onFilterClick={handleFilterClick}
            onCloseFilterPopup={handleCloseFilterPopup}
            onApplyFilters={handleApplyFilters}
            getSearchResults={getSearchResults}
            filterCategories={filterCategories}
          />
          {isLoading && (
            <div
              className="absolute left-0 right-0 flex flex-col items-center justify-center z-20"
              style={{ top: '64px', bottom: 0, background: 'rgba(255,255,255,0.9)' }}
            >
              <div className="w-10 h-10 border-4 border-secondary-200 border-t-secondary-700 rounded-full animate-spin mb-3"></div>
              <span className="text-lg text-secondary-700 font-semibold flex items-center">
                Loading
                <span className="inline-flex ml-1">
                  <span className="animate-bounce" style={{ animationDelay: '0ms' }}>
                    .
                  </span>
                  <span className="animate-bounce" style={{ animationDelay: '150ms' }}>
                    .
                  </span>
                  <span className="animate-bounce" style={{ animationDelay: '300ms' }}>
                    .
                  </span>
                </span>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Interview Modal */}
      <CandidateInterviewsModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSubmit={onSubmit}
        register={register}
        errors={errors}
        handleSubmit={handleSubmit}
        setValue={setValue}
        watch={watch}
        isSubmitting={isSubmitting}
        isValid={isValid}
        refVendor={refVendor}
        refVendorLoading={refVendorLoading}
        refTechnologies={refTechnologies}
        refTechnologiesLoading={refTechnologiesLoading}
        jobs={jobs}
        selectedJob={selectedJob}
        selectedJobDetails={selectedJobDetails}
        onJobChange={handleJobChange}
        salesPersons={salesPersons}
        salesPersonsLoading={salesPersonsLoading}
        isReadOnly={isReadOnly}
      />
    </div>
  );
};

export default Interviews;
